import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import DropDownKendoRct from "../../../control-components/drop-down/drop-down";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import DatePickerKendoRct from "../../../control-components/date-picker/date-picker";
import PhoneInput from "../../../control-components/phone-input-mask/phone-input-mask";
import InputKendoRct from "../../../control-components/input/input";
import { Error } from "@progress/kendo-react-labels";
import APP_ROUTES from "../../../helper/app-routes";
import { NotificationManager } from "src/util/notify";
import ApiHelper from "../../../helper/api-helper";
import ApiUrls from "../../../helper/api-urls";
import Loader from "../../../control-components/loader/loader";
import { useDispatch, useSelector } from "react-redux";
import {
  GET_SUBPOENA_MODULES,
  GET_SUBPOENA_REQUEST_DETAILS,
} from "../../../actions";
import { SubpoenaRequestService } from "../../../services/subpoenaRequestService";
import NOTIFICATION_MESSAGE from "../../../helper/notification-messages";
import moment from "moment";

const AddSubpoenaRequest = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const storedModules = useSelector((state) => state.subpoenaModules);
  const [fields, setFields] = useState({
    subpoenaRequestId: 0,
    clinic: null,
    startDate: null,
    endDate: null,
    clients: [],
    selectedModules: [],
    dueDate: null,
    phone: "",
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [clinicClients, setClinicClients] = useState([]);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [saving, setSaving] = useState(false);
  const [moduleSections, setModuleSections] = useState([]);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [clientFilter, setClientFilter] = useState("");

  const deriveSections = (modules) => {
    const sectionMap = new Map();
    modules.forEach((module) => {
      const sectionId = module.moduleSectionId;
      if (!sectionMap.has(sectionId)) {
        sectionMap.set(sectionId, {
          moduleSectionId: sectionId,
          moduleSectionName: module.moduleSectionName,
          modules: [],
        });
      }
      sectionMap.get(sectionId).modules.push({
        moduleId: module.moduleId,
        moduleName: module.moduleName,
      });
    });
    return Array.from(sectionMap.values());
  };

  const loadModules = async (regionId) => {
    if (!regionId) {
      setModuleSections([]);
      return;
    }
    try {
      const response = await SubpoenaRequestService.getSubpoenaModules(regionId);
      const modules = response?.resultData || [];
      dispatch({ type: GET_SUBPOENA_MODULES, payload: modules });
      setModuleSections(deriveSections(modules));
    } catch (error) {
      NOTIFICATION_MESSAGE.error(error);
      setModuleSections([]);
    }
  };

  const moduleGroups = useMemo(() => moduleSections, [moduleSections]);

  const filteredClients = useMemo(() => {
    if (!clientFilter) {
      return clinicClients;
    }
    const value = clientFilter.toLowerCase();
    return clinicClients.filter((client) =>
      (client.fullName || "")
        .toString()
        .toLowerCase()
        .includes(value)
    );
  }, [clinicClients, clientFilter]);

  const visibleModuleSections = useMemo(() => {
    return moduleGroups;
  }, [moduleGroups]);


  useEffect(() => {
    if (visibleModuleSections.length > 0 && !activeSectionId) {

      const sectionWithSelected = visibleModuleSections.find(section =>
        section.modules.some(module => fields.selectedModules.includes(module.moduleId))
      );


      const targetSection = sectionWithSelected || visibleModuleSections[0];
      setActiveSectionId(targetSection.moduleSectionId);
    }
  }, [visibleModuleSections, fields.selectedModules]);

  useEffect(() => {
    const loadData = async () => {
      await fetchClinics();
      if (state) {
        await getRequestById();
      }
    };
    loadData();
  }, []);

  const fetchClinics = async () => {
    setLoadingClinics(true);
    try {
      const response = await ApiHelper.getRequest(ApiUrls.ALL_CLINIC);
      const clinicList = (response?.resultData || []).map(clinic => ({
        ...clinic,
        clinicName: clinic.clinicName || clinic.name,
      }));
      setClinics(clinicList);
    } catch (error) {
      NotificationManager.error(error?.message || "Unable to load clinics.");
    } finally {
      setLoadingClinics(false);
    }
  };

  const getRequestById = async () => {
    setLoadingRequest(true);
    try {
      const regionId = state.regionId || state.id;
      const result = await SubpoenaRequestService.getSubpoenaRequestById(state.id, regionId);
      if (result.resultData && result.resultData.length > 0) {
        const data = result.resultData[0]; 
      
        
        const masterClinicId = data.masterClinicId 
        const matchedClinic = clinics.find(c => c.id === masterClinicId);
        const clinic = {
          id: masterClinicId,
          clinicName: data.clinicName,
          regionId: matchedClinic?.regionId || regionId
        };

        
        const selectedClients = (data.clients || []).map((c) => {
          const firstName = c.clientFirstName || "";
          const lastName = c.clientLastName || "";
          const fullName = `${firstName} ${lastName}`.trim();
          return {
            id: c.clientId ?? c.id,
            fullName: fullName,
          };
        });
        setFields({
          subpoenaRequestId: data.id,
          clinic: clinic,
          startDate: data.startDate ? new Date(data.startDate) : null,
          endDate: data.endDate ? new Date(data.endDate) : null,
          dueDate: data.dueDate ? new Date(data.dueDate) : null,
          phone: data.phoneNumber || "",
          email: data.emailAddress || "",
          selectedModules: data.modules
            ? data.modules.map((m) => m.moduleId)
            : [],
          clients: selectedClients,
        });

        if (clinic.regionId) {
          await loadModules(clinic.regionId);
        }

        await fetchClientsForClinic(masterClinicId);
      }
    } catch (error) {
      NOTIFICATION_MESSAGE.error(error);
    } finally {
      setLoadingRequest(false);
    }
  };

  const fetchClientsForClinic = async (masterClinicId) => {

    if (!masterClinicId) {
      setClinicClients([]);
      return;
    }
    setLoadingClients(true);
    const requestPayload = {
      pageNumber: 1,
      pageSize: 500,
      masterClinicId,
      clientId: 0,
      searchContent: "",
      isActive: 1,
    };

    try {
      const response = await ApiHelper.postRequest(
        ApiUrls.SEARCH_CLIENT,
        requestPayload
      );

      const clients =
        (response?.resultData || []).map((client) => ({
          ...client,
          fullName: [client.fName, client.lName]
            .filter((value) => !!value)
            .join(" ")
            .trim(),
        })) || [];

      setClinicClients(clients);
    } catch (error) {
      setClinicClients([]);
      NotificationManager.error(error?.message || "Unable to load clients.");
    } finally {
      setLoadingClients(false);
    }
  };


  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFields((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "clinic" ? { clients: [], selectedModules: [] } : {}),
    }));

    if (submitted && errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];

        if ((name === "startDate" || name === "endDate") && newErrors.dateRange) {
          delete newErrors.dateRange;
        }
        return newErrors;
      });
    }

    if (name === "clinic") {
      const masterClinicId = value?.id || value?.masterClinicId;
      const regionId = value?.regionId;
      
      if (regionId) {
        loadModules(regionId);
      } else {
        setModuleSections([]);
      }
      
      fetchClientsForClinic(masterClinicId);
      setClientFilter("");
    }
  };

  const handleClientsChange = (event) => {
    setFields((prev) => ({
      ...prev,
      clients: event.value,
    }));

    if (submitted && errors.clients) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.clients;
        return newErrors;
      });
    }
  };


  const toggleSection = (sectionId) => {
    setActiveSectionId(sectionId);
  };

  const handleModuleToggle = (moduleId) => {
    setFields((prev) => {
      const exists = prev.selectedModules.includes(moduleId);
      const nextModules = exists
        ? prev.selectedModules.filter((item) => item !== moduleId)
        : [...prev.selectedModules, moduleId];
      return {
        ...prev,
        selectedModules: nextModules,
      };
    });

    if (submitted && errors.selectedModules) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.selectedModules;
        return newErrors;
      });
    }
  };

  const handlePhoneChange = (event) => {
    const { name, rawValue } = event.target;
    setFields((prev) => ({
      ...prev,
      [name]: rawValue,
    }));

    if (submitted && errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!fields.clinic) {
      newErrors.clinic = "Clinic selection is required.";
    }

    if (!fields.startDate) {
      newErrors.startDate = "Start date is required.";
    }

    if (!fields.endDate) {
      newErrors.endDate = "End date is required.";
    }

    if (
      fields.startDate &&
      fields.endDate &&
      fields.endDate < fields.startDate
    ) {
      newErrors.dateRange = "End date must be after the start date.";
    }

    // if (!fields.clients || fields.clients.length === 0) {
    //   newErrors.clients = "Select at least one client.";
    // }
    
    // if (!fields.selectedModules || fields.selectedModules.length === 0) {
    //   newErrors.selectedModules = "Select at least one module to export.";
    // }

    if (!fields.dueDate) {
      newErrors.dueDate = "Request due date is required.";
    }

    const numericPhone = fields.phone.replace(/\D/g, "");
    if (numericPhone.length !== 10) {
      newErrors.phone = "Enter a valid 10-digit phone number.";
    }

    const emailPattern = /\S+@\S+\.\S+/;
    if (!emailPattern.test(fields.email)) {
      newErrors.email = "Enter a valid email address.";
    }

    return newErrors;
  };

  const resolveClientId = (client) =>
    client?.id ??
    client?.clientId ??
    client?.clientid ??
    client?.clientID ??
    client?.clientid;

  const buildPayload = () => {
    const clinicId =
      fields.clinic?.id ??
      fields.clinic?.masterClinicId ??
      fields.clinic?.clinicId ??
      0;
    const clientIds = (fields.clients || [])
      .map((client) => resolveClientId(client))
      .filter((id) => id !== undefined && id !== null);

    return {
      subpoenaRequestId: fields.subpoenaRequestId || 0,
      clinicId,
      startDate: fields.startDate
        ? moment(fields.startDate).startOf('day').format('M/D/YYYY')
        : null,
      endDate: fields.endDate ? moment(fields.endDate).startOf('day').format('M/D/YYYY') : null,
      dueDate: fields.dueDate ? moment(fields.dueDate).startOf('day').format('M/D/YYYY') : null,
      emailAddress: fields.email,
      phoneNumber: fields.phone,
      moduleIds: fields.selectedModules || [],
      clientIds: clientIds
    };
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setSaving(true);
      const payload = buildPayload();
      try {
        await SubpoenaRequestService.insertUpdateSubpoenaRequest(payload);
        NotificationManager.success("Subpoena request saved successfully.");
        dispatch({ type: GET_SUBPOENA_REQUEST_DETAILS, payload: false });
        navigate(APP_ROUTES.SUBPOENA_REQUEST);
      } catch (error) {
        NOTIFICATION_MESSAGE.error(error);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleCancel = () => {
    navigate(APP_ROUTES.SUBPOENA_REQUEST);
  };

  return (
    <>
      <div className="client-accept notenetic-container placeholder-size">
        <h4 className="address-title text-grey mb-5">
          <span className="f-24">
            {fields.subpoenaRequestId ? "Edit Subpoena Request" : "Add Subpoena Request"}
          </span>
        </h4>

        {(loadingClinics || loadingClients || loadingRequest || saving) && (
          <Loader />
        )}

        <div className="row inputFieldBox">
          <div className="mb-3 col-lg-6 col-md-6 col-12">
            <label className="form-label" style={{ fontSize: "0.875rem" }}>
              Clinic
            </label>
            <DropDownKendoRct
              data={clinics}
              textField="clinicName"
              dataItemKey="id"
              name="clinic"
              value={fields.clinic}
              onChange={handleFieldChange}
              required
              validityStyles={submitted}
              disabled={loadingClinics || fields.subpoenaRequestId > 0}
            />
            {submitted && errors.clinic && <Error>{errors.clinic}</Error>}
          </div>

          <div className="mb-3 col-lg-6 col-md-6 col-12 edit-Service-popup">
            <label className="form-label" style={{ fontSize: "0.875rem" }}>
              Clients (If none selected i.e all clients) 
            </label>
            <MultiSelect
              data={filteredClients}
              textField="fullName"
              dataItemKey="id"
              value={fields.clients}
              onChange={handleClientsChange}
              placeholder="Select clients"
              filterable
              onFilterChange={(event) => setClientFilter(event.filter.value)}
              filter={clientFilter}
              loading={loadingClients}
              disabled={!fields.clinic || loadingClients}
            />
            {/* {submitted && errors.clients && <Error>{errors.clients}</Error>} */}
          </div>

          <div className="mb-3 col-lg-6 col-md-6 col-12 date-field">
            <label className="form-label" style={{ fontSize: "0.875rem" }}>
              Data Requested From
            </label>
            <DatePickerKendoRct
              name="startDate"
              value={fields.startDate ? new Date(fields.startDate) : null}
              onChange={handleFieldChange}
              required
              validityStyles={submitted}
            />
            {submitted && errors.startDate && (
              <Error>{errors.startDate}</Error>
            )}
          </div>

          <div className="mb-3 col-lg-6 col-md-6 col-12 date-field">
            <label className="form-label" style={{ fontSize: "0.875rem" }}>
              Data Requested To
            </label>
            <DatePickerKendoRct
              name="endDate"
              value={fields.endDate ? new Date(fields.endDate) : null}
              onChange={handleFieldChange}
              required
              validityStyles={submitted}
            />
            {submitted && errors.endDate && (
              <Error>{errors.endDate}</Error>
            )}
          </div>

          {submitted && errors.dateRange && (
            <div className="col-12 mb-3">
              <Error>{errors.dateRange}</Error>
            </div>
          )}

          <div className="col-12">
            <p className="mb-3">Modules to Export for Subpoena (If none selected i.e all modules) </p>
            {visibleModuleSections.length > 0 ? (
              <div className="module-section-list">
                <div className="d-flex flex-wrap mb-3">
                  {visibleModuleSections.map((section) => (
                    <span
                      key={`tab-${section.moduleSectionId}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleSection(section.moduleSectionId)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          toggleSection(section.moduleSectionId);
                        }
                      }}
                      className="mr-3 mb-2"
                      style={{
                        cursor: "pointer",
                        fontWeight:
                          activeSectionId === section.moduleSectionId
                            ? 600
                            : 400,
                        borderBottom:
                          activeSectionId === section.moduleSectionId
                            ? "2px solid #007bff"
                            : "2px solid transparent",
                        paddingBottom: "4px",
                      }}
                    >
                      {section.moduleSectionName}
                    </span>
                  ))}
                </div>
                {visibleModuleSections.map((section) => (
                  <div key={section.moduleSectionId}>
                    {activeSectionId === section.moduleSectionId && (
                      <div className="pt-2">
                        {section.modules.map((module) => (
                          <label
                            className="form-check-label d-block mb-2"
                            key={module.moduleId}
                            style={{ cursor: "pointer", paddingLeft: "1.5rem" }}
                          >
                            <input
                              type="checkbox"
                              className="form-check-input mr-2"
                              checked={fields.selectedModules.includes(
                                module.moduleId
                              )}
                              onChange={() => handleModuleToggle(module.moduleId)}
                              style={{ marginLeft: "-1.5rem" }}
                            />
                            {module.moduleName}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3 bg-light border rounded text-muted">
                No modules available. Please select a clinic first.
              </div>
            )}
            {submitted && errors.selectedModules && (
              <Error>{errors.selectedModules}</Error>
            )}
          </div>

          <div className="mb-3 col-lg-4 col-md-6 col-12 date-field">
            <label className="form-label" style={{ fontSize: "0.875rem" }}>
              Request Due Date
            </label>
            <DatePickerKendoRct
              name="dueDate"
              value={fields.dueDate ? new Date(fields.dueDate) : null}
              onChange={handleFieldChange}
              required
              validityStyles={submitted}
            />
            {submitted && errors.dueDate && <Error>{errors.dueDate}</Error>}
          </div>

          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <label className="form-label" style={{ fontSize: "0.875rem" }}>
              Contact Phone
            </label>
            <PhoneInput
              name="phone"
              value={fields.phone}
              onChange={handlePhoneChange}
              validityStyles={submitted}
              required
            />
            {submitted && errors.phone && <Error>{errors.phone}</Error>}
          </div>

          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <label className="form-label" style={{ fontSize: "0.875rem" }}>
              Contact Email
            </label>
            <InputKendoRct
              name="email"
              value={fields.email}
              onChange={handleFieldChange}
              validityStyles={submitted}
              required
              placeholder="name@example.com"
            />
            {submitted && errors.email && <Error>{errors.email}</Error>}
          </div>
        </div>
      </div>

      <div className="d-flex my-4">
        <button
          className="green-btn btn-primary mr-3 btn-space"
          onClick={handleSubmit}
          disabled={saving}
        >
            {fields.subpoenaRequestId ? "Update Request" : "Submit Request"}
        </button>
        <button
          className="btn grey-secondary btn-space"
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </>
  );
};

export default AddSubpoenaRequest;

